/**
 * Utility functions for converting UTC date strings from API responses into user local timezone representations.
 */

/**
 * Formats UTC datetime string to local 12-hour date time string: YYYY/MM/DD, hh:mm:ss AM/PM
 * e.g. "2026/09/08, 08:49:37" (UTC) -> "2026/09/08, 02:19:37 PM" (in IST / UTC+5:30)
 */
export const formatToLocalDateString = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    let cleanStr = dateStr.trim().replace(/\//g, '-').replace(/,\s*/, 'T');
    if (!cleanStr.includes('T')) {
      cleanStr = cleanStr.replace(/\s+/, 'T');
    }
    if (!cleanStr.endsWith('Z') && !cleanStr.match(/[+-]\d{2}:\d{2}$/)) {
      cleanStr += 'Z';
    }
    const d = new Date(cleanStr);
    if (isNaN(d.getTime())) return dateStr;

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, '0');

    return `${year}/${month}/${day}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  } catch {
    return dateStr;
  }
};

/**
 * Formats UTC datetime string to local date time string in 12-hour format: YYYY-MM-DD | hh:mm AM/PM
 * e.g. "2026/09/08, 08:49:37" -> "2026-09-08 | 02:19 PM" (in IST / UTC+5:30)
 */
export const formatToLocal12HourDateString = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    let cleanStr = dateStr.trim().replace(/\//g, '-').replace(/,\s*/, 'T');
    if (!cleanStr.includes('T')) {
      cleanStr = cleanStr.replace(/\s+/, 'T');
    }
    if (!cleanStr.endsWith('Z') && !cleanStr.match(/[+-]\d{2}:\d{2}$/)) {
      cleanStr += 'Z';
    }
    const d = new Date(cleanStr);
    if (isNaN(d.getTime())) return dateStr;

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, '0');

    return `${year}-${month}-${day} | ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return dateStr;
  }
};
